import React, { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';

import {
  AIOperatorAnalytics as AIOperatorAnalyticsData,
  RetrieverVisibilityComparison,
  RetrieverVisibilityExample,
  SimulationProgressData,
} from './types';
import {
  PLOTLY_LAYOUT_BASE,
  TimeWindowKey,
  colorForIndex,
  cutoffForWindow,
  filterByCreatedAt,
  formatRating,
  truncate,
} from './chartHelpers';
import TimeWindowSelector from './TimeWindowSelector';
import CommentHeatmapCard from './CommentHeatmapCard';

interface Props {
  data: AIOperatorAnalyticsData;
  generatedAt: string | null;
}

const RATING_COLORSCALE = 'RdYlGn' as any;

const AIOperatorAnalytics: React.FC<Props> = ({ data, generatedAt }) => {
  const pca = data.pca || { points: [], explained_variance: [], sample_size: 0 };
  const clusters = data.clusters || [];
  const [pcaWindow, setPcaWindow] = useState<TimeWindowKey>('all');

  const filteredPcaPoints = useMemo(() => {
    const cutoff = cutoffForWindow(generatedAt, pcaWindow);
    return filterByCreatedAt(pca.points || [], cutoff);
  }, [pca.points, generatedAt, pcaWindow]);
  const ratingByCluster = data.rating_by_cluster || [];
  const ratingBySimulation = data.rating_by_simulation || [];
  const ratingBySituation = data.rating_by_situation || [];
  const ratingByAddress = data.rating_by_address || [];
  const ratingByDocument = data.rating_by_document || [];
  const ratingBySearchType = data.rating_by_search_type || { per_type: [], overlap: [] };
  const retrieverVisibility = data.retriever_visibility_comparison || null;
  const ratingOverTime = data.rating_over_time || [];
  const simulationProgress = data.simulation_progress || null;

  const clusterTraces = useMemo(() => {
    const groups = new Map<number, typeof pca.points>();
    filteredPcaPoints.forEach(p => {
      const cid = (p as any).cluster_id ?? -1;
      if (!groups.has(cid)) groups.set(cid, [] as any);
      groups.get(cid)!.push(p);
    });
    const clusterLookup = new Map(clusters.map(c => [c.cluster_id, c]));
    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([cid, points], idx) => {
        const summary = clusterLookup.get(cid);
        const labelKeywords = summary?.label_keywords?.filter(Boolean) || [];
        const labelText = labelKeywords.length > 0
          ? labelKeywords.slice(0, 3).join(', ')
          : `Cluster ${cid}`;
        const ratingText = formatRating(summary?.avg_rating ?? null);
        return {
          x: points.map(p => p.x),
          y: points.map(p => p.y),
          mode: 'markers' as const,
          type: 'scattergl' as const,
          name: `${labelText} (avg ${ratingText})`,
          marker: { size: 8, color: colorForIndex(idx), opacity: 0.78 },
          hovertemplate:
            '<b>%{customdata[0]}</b><br>' +
            'rating: %{customdata[1]}<br>' +
            'situation: %{customdata[2]}<br>' +
            'doc: %{customdata[3]}<extra></extra>',
          customdata: points.map(p => [
            truncate((p as any).message_snippet, 120) || '(empty)',
            (p as any).rating ?? 'n/a',
            (p as any).situation_name || '-',
            truncate((p as any).document_id || '-', 40),
          ]),
        };
      });
  }, [filteredPcaPoints, clusters]);

  const explained = (pca.explained_variance || []).map(v => `${(v * 100).toFixed(1)}%`);

  const renderGroupedBar = (
    title: string,
    rows: { label: string; avg_rating: number | null; count: number }[],
    hint: string,
  ) => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-xs text-gray-500 mb-3">{hint}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing to chart yet.</p>
      ) : (
        <Plot
          data={[
            {
              type: 'bar',
              orientation: 'h',
              x: rows.slice(0, 25).map(r => r.avg_rating ?? 0).reverse(),
              y: rows.slice(0, 25).map(r => truncate(r.label, 50)).reverse(),
              marker: {
                color: rows.slice(0, 25).map(r => r.avg_rating ?? 0).reverse(),
                colorscale: RATING_COLORSCALE,
                cmin: 1,
                cmax: 5,
                showscale: true,
                colorbar: { title: { text: 'avg rating', side: 'right' } as any },
              },
              text: rows.slice(0, 25).map(r => `n=${r.count}`).reverse(),
              hovertemplate: '%{y}<br>avg rating: %{x:.2f}<br>%{text}<extra></extra>',
            },
          ]}
          layout={{
            ...PLOTLY_LAYOUT_BASE,
            height: Math.max(320, Math.min(25, rows.length) * 24 + 80),
            xaxis: { title: 'Average rating', range: [0, 5] },
            yaxis: { automargin: true },
            margin: { ...PLOTLY_LAYOUT_BASE.margin, l: 220 },
          }}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
          useResizeHandler
        />
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h3 className="text-lg font-semibold">Semantic PCA + clusters</h3>
          <TimeWindowSelector value={pcaWindow} onChange={setPcaWindow} />
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Each point is one AI operator message; colour = KMeans cluster (labels via TF-IDF keywords).
          {' The PCA basis is fitted on all '}
          <span className="font-medium text-gray-700">{(pca.points || []).length.toLocaleString()}</span>
          {' messages; the selected window only filters which messages are drawn.'}
          {explained.length === 2 && ` Explained variance: PC1 ${explained[0]}, PC2 ${explained[1]}.`}
          {' Showing '}
          <span className="font-medium text-gray-700">
            {filteredPcaPoints.length.toLocaleString()} of {(pca.points || []).length.toLocaleString()}
          </span>
          {' messages.'}
        </p>
        {pca.warning ? (
          <p className="text-sm text-gray-500">{pca.warning}</p>
        ) : clusterTraces.length === 0 ? (
          <p className="text-sm text-gray-500">
            {(pca.points || []).length === 0
              ? 'No PCA points available.'
              : 'No messages fall inside the selected window.'}
          </p>
        ) : (
          <Plot
            data={clusterTraces as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 540,
              xaxis: { title: 'PC1', zeroline: false },
              yaxis: { title: 'PC2', zeroline: false },
              showlegend: true,
              legend: { orientation: 'v', x: 1.02, y: 1 },
            }}
            config={{ responsive: true, displaylogo: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        )}
      </div>

      {renderGroupedBar(
        'Average rating by cluster',
        ratingByCluster.map(c => ({
          label: `Cluster ${c.cluster_id}`,
          avg_rating: c.avg_rating,
          count: c.count,
        })),
        'Average AI operator rating per semantic cluster.',
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderGroupedBar(
          'Average rating by simulation',
          ratingBySimulation,
          'Top simulations by feedback count.',
        )}
        {renderGroupedBar(
          'Average rating by situation',
          ratingBySituation,
          'Average AI operator rating per situation.',
        )}
        {renderGroupedBar(
          'Average rating by address',
          ratingByAddress,
          'Average AI operator rating per incident address (one level below situation).',
        )}
        {renderGroupedBar(
          'Average rating by document',
          ratingByDocument,
          'Documents referenced by the AI operator message.',
        )}
        <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-1">Average rating by search-type membership</h3>
          <p className="text-xs text-gray-500 mb-3">
            For each rated message, did the cited document appear via oasis, semantic, or both retrievers?
          </p>
          {ratingBySearchType.per_type.length === 0 && ratingBySearchType.overlap.length === 0 ? (
            <p className="text-sm text-gray-500">No data yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Per search type</h4>
                {ratingBySearchType.per_type.length === 0 ? (
                  <p className="text-sm text-gray-500">No per-type data yet.</p>
                ) : (
                  <Plot
                    data={[
                      {
                        type: 'bar',
                        x: ratingBySearchType.per_type.map(r => r.search_type),
                        y: ratingBySearchType.per_type.map(r => r.avg_rating ?? 0),
                        marker: {
                          color: ratingBySearchType.per_type.map(r => r.avg_rating ?? 0),
                          colorscale: RATING_COLORSCALE,
                          cmin: 1,
                          cmax: 5,
                          showscale: false,
                        },
                        text: ratingBySearchType.per_type.map(r => `n=${r.count}`),
                        hovertemplate: '%{x}<br>avg rating: %{y:.2f}<br>%{text}<extra></extra>',
                      },
                    ] as any}
                    layout={{
                      ...PLOTLY_LAYOUT_BASE,
                      height: 320,
                      yaxis: { title: 'Average rating', range: [0, 5] },
                      xaxis: { title: 'Search type' },
                      margin: { ...PLOTLY_LAYOUT_BASE.margin, b: 60 },
                    }}
                    config={{ responsive: true, displayModeBar: false }}
                    style={{ width: '100%' }}
                    useResizeHandler
                  />
                )}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Overlap groups</h4>
                {ratingBySearchType.overlap.length === 0 ? (
                  <p className="text-sm text-gray-500">No overlap data yet.</p>
                ) : (
                  <Plot
                    data={[
                      {
                        type: 'bar',
                        x: ratingBySearchType.overlap.map(r => r.overlap),
                        y: ratingBySearchType.overlap.map(r => r.avg_rating ?? 0),
                        marker: {
                          color: ratingBySearchType.overlap.map(r => r.avg_rating ?? 0),
                          colorscale: RATING_COLORSCALE,
                          cmin: 1,
                          cmax: 5,
                          showscale: false,
                        },
                        text: ratingBySearchType.overlap.map(r => `n=${r.count}`),
                        hovertemplate: '%{x}<br>avg rating: %{y:.2f}<br>%{text}<extra></extra>',
                      },
                    ] as any}
                    layout={{
                      ...PLOTLY_LAYOUT_BASE,
                      height: 320,
                      yaxis: { title: 'Average rating', range: [0, 5] },
                      xaxis: { title: 'Overlap label' },
                      margin: { ...PLOTLY_LAYOUT_BASE.margin, b: 60 },
                    }}
                    config={{ responsive: true, displayModeBar: false }}
                    style={{ width: '100%' }}
                    useResizeHandler
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {renderRetrieverVisibilityCard(retrieverVisibility)}

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-1">Average AI operator rating over time</h3>
        <p className="text-xs text-gray-500 mb-3">
          ISO weekly buckets; each tick is the Monday of that week. Feedback rows with future
          timestamps are filtered out server-side (and logged), so anything past today on this chart
          would indicate a fresh data issue.
        </p>
        {ratingOverTime.length === 0 ? (
          <p className="text-sm text-gray-500">No feedback recorded yet.</p>
        ) : (
          <Plot
            data={[
              {
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Average rating',
                x: ratingOverTime.map(r => r.week_start || r.bucket),
                y: ratingOverTime.map(r => r.avg_rating),
                text: ratingOverTime.map(r => r.bucket),
                line: { color: '#2563EB', width: 2 },
                marker: { color: '#2563EB', size: 6 },
                yaxis: 'y',
                hovertemplate:
                  'Week of %{x|%b %d, %Y} (%{text})<br>avg rating: %{y:.2f}<extra></extra>',
              },
              {
                type: 'bar',
                name: 'Count',
                x: ratingOverTime.map(r => r.week_start || r.bucket),
                y: ratingOverTime.map(r => r.count),
                text: ratingOverTime.map(r => r.bucket),
                marker: { color: 'rgba(124, 58, 237, 0.35)' },
                yaxis: 'y2',
                hovertemplate:
                  'Week of %{x|%b %d, %Y} (%{text})<br>feedback rows: %{y}<extra></extra>',
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 380,
              xaxis: {
                title: 'Week (Monday)',
                type: ratingOverTime[0]?.week_start ? 'date' : 'category',
                tickformat: '%b %d, %Y',
              } as any,
              yaxis: { title: 'Average rating', range: [0, 5] },
              yaxis2: { title: 'Count', overlaying: 'y', side: 'right', showgrid: false } as any,
              showlegend: true,
              legend: { orientation: 'h', y: -0.25 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        )}
      </div>

      {renderSimulationProgressCard(simulationProgress)}

      <CommentHeatmapCard
        title="Semantic heatmap of AI Operator feedback comments"
        description="Free-text comments operators left when rating an AI Operator message, embedded with mistral-embed and projected to 2D. Dot colour shows the rating they gave; the blue contour shows comment density."
        payload={data.comment_heatmap || null}
        generatedAt={generatedAt}
        showRating
        extraHoverFields={[
          { label: 'situation', pick: p => p.situation_name },
          { label: 'address', pick: p => p.address_label },
          { label: 'doc', pick: p => p.document_id },
        ]}
      />
    </div>
  );
};

const VISIBILITY_BUCKET_ORDER = ['oasis_only', 'semantic_only', 'both', 'reranked_only'] as const;
const VISIBILITY_BUCKET_LABEL: Record<string, string> = {
  oasis_only: 'oasis only',
  semantic_only: 'semantic only',
  both: 'oasis + semantic',
  reranked_only: 'rerank only',
};
const VISIBILITY_BUCKET_COLOUR: Record<string, string> = {
  oasis_only: '#2563EB',
  semantic_only: '#7C3AED',
  both: '#0EA5E9',
  reranked_only: '#16A34A',
};

function renderRetrieverVisibilityCard(comparison: RetrieverVisibilityComparison | null) {
  if (!comparison) return null;
  const k = comparison.top_k;
  const counts = comparison.bucket_counts || [];
  const distribution = comparison.rating_distribution_by_bucket || {};
  const totalMessages = counts.reduce((acc, c) => acc + c.count, 0);
  const examples = comparison.examples || {};

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-5">
      <div>
        <h3 className="text-lg font-semibold mb-1">
          Retriever visibility of cited documents (top-{k})
        </h3>
        <p className="text-xs text-gray-500">
          Every rated AI Operator message cites a document. Production serves{' '}
          <code>combined_reranked</code>, so the cited doc is virtually always in rerank's top-{k}.
          The interesting question is whether the same doc would also have been visible inside
          oasis's or semantic's top-{k} — i.e. whether either base retriever alone would have
          surfaced the citation. Buckets: <strong>oasis only</strong> = oasis &amp; rerank had it
          but semantic missed; <strong>semantic only</strong> = mirror; <strong>oasis + semantic</strong>{' '}
          = both base retrievers agree; <strong>rerank only</strong> = rerank uniquely pulled it from
          outside both base top-{k}s. {totalMessages.toLocaleString()} rated messages in scope.
        </p>
      </div>

      {totalMessages === 0 ? (
        <p className="text-sm text-gray-500">No rated messages with retrieved documents yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Plot
              data={[
                {
                  type: 'bar',
                  x: counts.map(c => VISIBILITY_BUCKET_LABEL[c.bucket] || c.bucket),
                  y: counts.map(c => c.count),
                  marker: {
                    color: counts.map(c => VISIBILITY_BUCKET_COLOUR[c.bucket] || '#64748B'),
                  },
                  text: counts.map(c =>
                    c.avg_rating == null ? `n=${c.count}` : `n=${c.count}\u2003avg ${c.avg_rating.toFixed(2)}`,
                  ),
                  textposition: 'outside',
                  hovertemplate:
                    '%{x}<br>messages: %{y}<br>avg rating: %{customdata[0]}<br>median: %{customdata[1]}<extra></extra>',
                  customdata: counts.map(c => [
                    c.avg_rating == null ? 'n/a' : c.avg_rating.toFixed(2),
                    c.median_rating == null ? 'n/a' : c.median_rating.toFixed(2),
                  ]),
                },
              ] as any}
              layout={{
                ...PLOTLY_LAYOUT_BASE,
                height: 360,
                title: { text: 'Messages per visibility bucket', font: { size: 13 } } as any,
                yaxis: { title: 'Rated messages' },
                xaxis: { title: `Visibility in top-${k}` },
                margin: { ...PLOTLY_LAYOUT_BASE.margin, t: 40 },
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
            <Plot
              data={VISIBILITY_BUCKET_ORDER.filter(b => (distribution[b] || []).length > 0).map(
                bucket => ({
                  type: 'box',
                  name: VISIBILITY_BUCKET_LABEL[bucket] || bucket,
                  y: distribution[bucket],
                  boxpoints: 'all',
                  jitter: 0.5,
                  pointpos: 0,
                  marker: { color: VISIBILITY_BUCKET_COLOUR[bucket], size: 5, opacity: 0.6 },
                  line: { color: VISIBILITY_BUCKET_COLOUR[bucket] },
                  hovertemplate: '%{y}<extra></extra>',
                }),
              ) as any}
              layout={{
                ...PLOTLY_LAYOUT_BASE,
                height: 360,
                title: { text: 'Rating distribution per bucket', font: { size: 13 } } as any,
                yaxis: { title: 'Operator rating', range: [0.5, 5.5], dtick: 1 },
                xaxis: { title: `Visibility in top-${k}` },
                showlegend: false,
                margin: { ...PLOTLY_LAYOUT_BASE.margin, t: 40 },
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-2 text-gray-800">Side-by-side examples</h4>
            <p className="text-xs text-gray-500 mb-3">
              Highest-rated AI Operator messages per "interesting" bucket — the consensus{' '}
              <em>oasis + semantic</em> case is hidden since both base retrievers agree. Read across
              columns to compare what oasis-unique, semantic-unique and rerank-unique picks actually
              look like in production.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {(['oasis_only', 'semantic_only', 'reranked_only'] as const).map(bucket =>
                renderVisibilityExamplesList(bucket, examples[bucket] || []),
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function renderVisibilityExamplesList(bucket: string, rows: RetrieverVisibilityExample[]) {
  const label = VISIBILITY_BUCKET_LABEL[bucket] || bucket;
  const colour = VISIBILITY_BUCKET_COLOUR[bucket] || '#64748B';
  return (
    <div key={bucket} className="border border-gray-200 rounded-md overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white"
        style={{ backgroundColor: colour }}
      >
        {label} ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 p-3">No messages in this bucket.</p>
      ) : (
        <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
          {rows.map(r => (
            <div key={r.feedback_id} className="p-3 text-xs">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <div className="font-semibold text-gray-900 tabular-nums">
                  Rating {r.rating}
                </div>
                <div className="text-[10px] text-gray-500 tabular-nums">
                  ranks: oasis {r.oasis_rank ?? '\u2014'} · semantic {r.semantic_rank ?? '\u2014'} ·
                  rerank {r.reranked_rank ?? '\u2014'}
                </div>
              </div>
              {r.message_snippet && (
                <p className="text-gray-800 mb-1.5 leading-snug">
                  &ldquo;{r.message_snippet}&rdquo;
                </p>
              )}
              <div className="text-[10px] text-gray-500 space-y-0.5">
                <div>
                  <span className="text-gray-400">doc:</span>{' '}
                  {truncate(r.document_label || r.document_id || '\u2014', 70)}
                </div>
                {r.situation_name && (
                  <div>
                    <span className="text-gray-400">situation:</span>{' '}
                    {truncate(r.situation_name, 60)}
                  </div>
                )}
                {r.address_label && (
                  <div>
                    <span className="text-gray-400">address:</span>{' '}
                    {truncate(r.address_label, 60)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderSimulationProgressCard(progress: SimulationProgressData | null) {
  if (!progress) return null;
  const perSim = progress.per_simulation || [];
  const buckets = progress.by_event_count_bucket || [];
  const isEmpty = perSim.length === 0;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">Trends by simulation maturity</h3>
      <p className="text-xs text-gray-500 mb-3">
        One dot per simulation along the x-axis (number of events in that simulation). Faded dots are
        per-simulation values; bold lines connect the mean inside log-spaced event-count buckets
        ({buckets.map(b => b.bucket_label).join(', ') || 'no buckets yet'}). Ratings share the left
        axis (0&ndash;5); characteristic corrections use the right axis (count).
      </p>
      {isEmpty ? (
        <p className="text-sm text-gray-500">No simulations with events yet.</p>
      ) : (
        <Plot
          data={[
            {
              type: 'scatter',
              mode: 'markers',
              name: 'AI op rating (per sim)',
              x: perSim.map(r => r.event_count),
              y: perSim.map(r => r.avg_aiop_rating),
              marker: { color: '#2563EB', size: 6, opacity: 0.35, symbol: 'circle' },
              yaxis: 'y',
              hovertemplate:
                'sim #%{customdata[0]}<br>events: %{x}<br>AI op avg: %{y:.2f} (n=%{customdata[1]})<extra></extra>',
              customdata: perSim.map(r => [r.simulation_id, r.aiop_rating_count]),
              legendgroup: 'aiop',
              showlegend: false,
            },
            {
              type: 'scatter',
              mode: 'markers',
              name: 'Doc search rating (per sim)',
              x: perSim.map(r => r.event_count),
              y: perSim.map(r => r.avg_doc_search_rating),
              marker: { color: '#7C3AED', size: 6, opacity: 0.35, symbol: 'diamond' },
              yaxis: 'y',
              hovertemplate:
                'sim #%{customdata[0]}<br>events: %{x}<br>doc search avg: %{y:.2f} (n=%{customdata[1]})<extra></extra>',
              customdata: perSim.map(r => [r.simulation_id, r.doc_search_rating_count]),
              legendgroup: 'docsearch',
              showlegend: false,
            },
            {
              type: 'scatter',
              mode: 'markers',
              name: 'Corrections (per sim)',
              x: perSim.map(r => r.event_count),
              y: perSim.map(r => r.correction_count),
              marker: { color: '#16A34A', size: 6, opacity: 0.35, symbol: 'square' },
              yaxis: 'y2',
              hovertemplate:
                'sim #%{customdata[0]}<br>events: %{x}<br>corrections: %{y}<extra></extra>',
              customdata: perSim.map(r => [r.simulation_id]),
              legendgroup: 'corrections',
              showlegend: false,
            },
            {
              type: 'scatter',
              mode: 'lines+markers',
              name: 'AI op rating (bucket mean)',
              x: buckets.map(b => b.mean_event_count),
              y: buckets.map(b => b.avg_aiop_rating),
              line: { color: '#2563EB', width: 3 },
              marker: { color: '#2563EB', size: 10, symbol: 'circle' },
              yaxis: 'y',
              text: buckets.map(b => b.bucket_label),
              hovertemplate:
                'bucket %{text}<br>~%{x:.1f} events<br>AI op avg: %{y:.2f} (sims=%{customdata[0]}, n=%{customdata[1]})<extra></extra>',
              customdata: buckets.map(b => [b.simulation_count, b.avg_aiop_rating_n]),
              legendgroup: 'aiop',
            },
            {
              type: 'scatter',
              mode: 'lines+markers',
              name: 'Doc search rating (bucket mean)',
              x: buckets.map(b => b.mean_event_count),
              y: buckets.map(b => b.avg_doc_search_rating),
              line: { color: '#7C3AED', width: 3, dash: 'dot' },
              marker: { color: '#7C3AED', size: 10, symbol: 'diamond' },
              yaxis: 'y',
              text: buckets.map(b => b.bucket_label),
              hovertemplate:
                'bucket %{text}<br>~%{x:.1f} events<br>doc search avg: %{y:.2f} (sims=%{customdata[0]}, n=%{customdata[1]})<extra></extra>',
              customdata: buckets.map(b => [b.simulation_count, b.avg_doc_search_rating_n]),
              legendgroup: 'docsearch',
            },
            {
              type: 'scatter',
              mode: 'lines+markers',
              name: 'Corrections (bucket mean)',
              x: buckets.map(b => b.mean_event_count),
              y: buckets.map(b => b.avg_correction_count),
              line: { color: '#16A34A', width: 3, dash: 'dash' },
              marker: { color: '#16A34A', size: 10, symbol: 'square' },
              yaxis: 'y2',
              text: buckets.map(b => b.bucket_label),
              hovertemplate:
                'bucket %{text}<br>~%{x:.1f} events<br>avg corrections: %{y:.2f} (sims=%{customdata[0]}, total=%{customdata[1]})<extra></extra>',
              customdata: buckets.map(b => [b.simulation_count, b.total_correction_count]),
              legendgroup: 'corrections',
            },
          ] as any}
          layout={{
            ...PLOTLY_LAYOUT_BASE,
            height: 460,
            xaxis: {
              title: 'Events per simulation (log scale)',
              type: 'log' as any,
              autorange: true,
            },
            yaxis: {
              title: 'Average rating (1\u20135)',
              range: [0, 5.2],
            },
            yaxis2: {
              title: 'Characteristic corrections',
              overlaying: 'y',
              side: 'right',
              showgrid: false,
              rangemode: 'tozero',
            } as any,
            showlegend: true,
            legend: { orientation: 'h', y: -0.2 },
            hovermode: 'closest',
          }}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
          useResizeHandler
        />
      )}
    </div>
  );
}

export default AIOperatorAnalytics;
